import { useState } from "react";
import axios from "axios";
import { API } from "../../config";
import { FileText, Loader2, CheckCircle, Globe, Edit3, ChevronDown, ArrowRight } from "lucide-react";

export const BlogPackGenerator = ({ t, language, userEmail }) => {
  const [url, setUrl] = useState('');
  const [niche, setNiche] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [proposedTitles, setProposedTitles] = useState(null);
  const [agentId, setAgentId] = useState(null);
  const [editingTitle, setEditingTitle] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [approved, setApproved] = useState(false);

  const handleStart = async () => {
    if (!url.trim()) return;
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/blog-agent/start`, {
        url: url.trim(),
        email: userEmail || 'guest@toprankpulse.com',
        language,
        niche: niche.trim()
      });
      setAgentId(res.data.agent_id);
      setProposedTitles(res.data.proposed_titles);
    } catch (e) {
      console.error('Blog agent error:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditTitle = (i, newTitle) => {
    const updated = [...proposedTitles];
    updated[i] = { ...updated[i], title: newTitle };
    setProposedTitles(updated);
    setEditingTitle(null);
  };

  const handleApprove = async () => {
    if (!agentId) return;
    setIsLoading(true);
    try {
      await axios.post(`${API}/blog-agent/${agentId}/approve`, { titles: proposedTitles });
      setApproved(true);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="py-24 px-6 bg-[var(--color-bg)] border-t border-[var(--color-border)]" id="blog-pack" data-testid="blog-pack-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#FFE600]/10 text-[#FFE600] text-sm font-['JetBrains_Mono'] tracking-[0.2em] uppercase mb-4">
            <FileText className="w-4 h-4" />
            {t.ba_tag || "AI Blog Agent"}
          </span>
          <h2 className="text-4xl sm:text-5xl font-black font-['Cabinet_Grotesk'] tracking-tighter text-white mb-4">
            {t.ba_h1_1 || "25 SEO ARTICLES."}<br />{t.ba_h1_2 || "1 PER DAY."}<br /><span className="text-[#FFE600]">{t.ba_h1_3 || "€25."}</span>
          </h2>
          <p className="text-[var(--color-text-muted)] text-lg max-w-2xl mx-auto">
            {t.ba_desc || "AI generates a daily SEO article with backlinks to your website, published on our blog. 25 days = 25 articles = 100 backlinks."}
          </p>
        </div>

        {/* Step 1: Input URL */}
        {!proposedTitles && !approved && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-8">
              <p className="text-[var(--color-text-muted)] font-['JetBrains_Mono'] text-xs uppercase tracking-wider mb-4">{t.ba_step1 || "Step 1: Enter your website"}</p>
              <div className="space-y-4">
                <input data-testid="blogpack-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder={t.ba_url_ph || "https://yourwebsite.com"} className="w-full px-4 py-3 bg-[var(--color-bg)] border border-[var(--color-border)] text-white focus:border-[#FFE600] focus:outline-none font-['JetBrains_Mono']" />
                <input data-testid="blogpack-niche" value={niche} onChange={(e) => setNiche(e.target.value)} placeholder={t.ba_niche_ph || "Niche / Topics (optional)"} className="w-full px-4 py-3 bg-[var(--color-bg)] border border-[var(--color-border)] text-white focus:border-[#FFE600] focus:outline-none" />
                <button data-testid="blogpack-generate" onClick={handleStart} disabled={isLoading || !url.trim()} className="w-full py-4 bg-[#FFE600] text-black font-black text-lg uppercase tracking-wider hover:bg-yellow-300 transition-all disabled:opacity-50 flex items-center justify-center gap-3">
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
                  {isLoading ? (t.ba_loading || 'AI IS PROPOSING TITLES...') : (t.ba_btn || 'GET 25 ARTICLE PROPOSALS — €25')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Approve titles */}
        {proposedTitles && !approved && (
          <div className="max-w-3xl mx-auto" data-testid="title-approval">
            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6 mb-4">
              <p className="text-[var(--color-text-muted)] font-['JetBrains_Mono'] text-xs uppercase tracking-wider mb-2">{t.ba_step2_title || "Step 2: Review & approve article titles"}</p>
              <p className="text-[var(--color-text-muted)] text-sm">{t.ba_step2_desc || "Click any title to edit it. Once approved, you'll receive 1 article per day."}</p>
            </div>
            <div className="space-y-2 mb-6">
              {proposedTitles.map((item, i) => (
                <div key={i} className="bg-[var(--color-surface)] border border-[var(--color-border)] p-4 flex items-center gap-4 group hover:border-[var(--color-primary)] transition-colors" data-testid={`title-${i}`}>
                  <span className="text-[var(--color-text-muted)] font-['JetBrains_Mono'] text-sm w-8">#{i + 1}</span>
                  {editingTitle === i ? (
                    <div className="flex-1 flex gap-2">
                      <input value={editValue} onChange={(e) => setEditValue(e.target.value)} className="flex-1 px-3 py-1 bg-[var(--color-bg)] border border-zinc-600 text-white text-sm focus:outline-none focus:border-[#FFE600]" autoFocus />
                      <button onClick={() => handleEditTitle(i, editValue)} className="px-3 py-1 bg-emerald-600 text-white text-sm font-bold">{t.ba_save || "Save"}</button>
                      <button onClick={() => setEditingTitle(null)} className="px-3 py-1 text-[var(--color-text-muted)] text-sm">{t.ba_cancel || "Cancel"}</button>
                    </div>
                  ) : (
                    <div className="flex-1 flex items-center justify-between">
                      <div>
                        <p className="text-white text-sm font-medium">{item.title}</p>
                        <p className="text-[var(--color-text-muted)] text-xs mt-0.5">{item.target_keyword} — ~{item.search_volume} {t.ba_searches || "searches/mo"} — {item.category}</p>
                      </div>
                      <button onClick={() => { setEditingTitle(i); setEditValue(item.title); }} className="opacity-0 group-hover:opacity-100 transition-opacity text-[var(--color-text-muted)] hover:text-[var(--color-text)] p-1">
                        <Edit3 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <button data-testid="approve-titles" onClick={handleApprove} disabled={isLoading} className="w-full py-4 bg-emerald-600 text-white font-black text-lg uppercase tracking-wider hover:bg-emerald-500 transition-all disabled:opacity-50 flex items-center justify-center gap-3">
              {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
              {t.ba_approve || "APPROVE & START DAILY GENERATION"}
            </button>
          </div>
        )}

        {/* Step 3: Approved */}
        {approved && (
          <div className="max-w-2xl mx-auto text-center" data-testid="approved-confirmation">
            <div className="bg-emerald-900/20 border border-emerald-800 p-8">
              <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
              <h3 className="text-2xl font-black font-['Cabinet_Grotesk'] text-white mb-2">{t.ba_activated || "Blog Agent Activated!"}</h3>
              <p className="text-[var(--color-text-muted)] mb-6">{t.ba_activated_desc || "Your first article will be generated within 1 hour. You'll receive a new article every 24 hours for the next 25 days."}</p>
              <div className="flex items-center justify-center gap-4">
                <a href="/dashboard" className="px-6 py-3 bg-[#a855f7] text-white font-bold hover:bg-purple-600 transition-colors flex items-center gap-2">
                  {t.ba_view_dash || "View in Dashboard"} <ArrowRight className="w-4 h-4" />
                </a>
                <a href="/blog" className="px-6 py-3 border border-[var(--color-border)] text-[var(--color-text)] font-bold hover:border-zinc-500 transition-colors">
                  {t.ba_visit_blog || "Visit Blog"}
                </a>
              </div>
            </div>
          </div>
        )}

        {/* How it works */}
        {!proposedTitles && !approved && (
          <div className="grid md:grid-cols-4 gap-4 mt-16 max-w-4xl mx-auto">
            {[
              { step: "01", title: t.ba_s1 || "Enter URL", desc: t.ba_s1_desc || "Tell us your website" },
              { step: "02", title: t.ba_s2 || "Approve titles", desc: t.ba_s2_desc || "AI proposes 25 topics — edit if needed" },
              { step: "03", title: t.ba_s3 || "Daily articles", desc: t.ba_s3_desc || "1 article published per day on our blog" },
              { step: "04", title: t.ba_s4 || "SEO boost", desc: t.ba_s4_desc || "100 backlinks to your site in 25 days" },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <span className="text-[#a855f7] font-['JetBrains_Mono'] text-3xl font-bold">{s.step}</span>
                <h4 className="text-white font-bold mt-2">{s.title}</h4>
                <p className="text-[var(--color-text-muted)] text-sm mt-1">{s.desc}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
